import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    useMap,
    useMapEvents,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { router, usePage } from "@inertiajs/react";
import { ReactSVG } from "react-svg";
import arrowsearch from "@/assets/Arrowsearch.svg";
import TransLink from "@/utils/TransLink";
import Star from "@/components/Star";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import close from "@/assets/close-circle.svg";
import { Button, useDisclosure } from "@heroui/react";
import { useTranslation } from "react-i18next";
import Directions from "@/components/Directions";

import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter
} from "@heroui/modal";
import PopupMap from "../Plan/components/PopupMap";
import { SearchItem } from "@/types";

// Custom SVG Marker
const CustomSVGMarker = (color, isHovered) => {
    return `
    <svg width="45" height="45" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg" class="${isHovered ? "animate-bounce" : ""}">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M21.0999 9.25228C19.1399 0.59228 6.30992 0.58228 4.33992 9.24228C3.18992 14.3223 6.34992 18.6323 9.10992 21.2923C11.1299 23.2323 14.3099 23.2323 16.3199 21.2923C19.0899 18.6323 22.2499 14.3323 21.0999 9.25228ZM15.8403 11.0623C15.8403 12.7854 14.4435 14.1823 12.7203 14.1823C10.9972 14.1823 9.60034 12.7854 9.60034 11.0623C9.60034 9.33918 10.9972 7.94231 12.7203 7.94231C14.4435 7.94231 15.8403 9.33918 15.8403 11.0623Z" fill="${color}"/>
    </svg>
  `;
};

// Function to create custom icon
export const createCustomIcon = (color, isHovered) => {
    return L.divIcon({
        html: CustomSVGMarker(color, isHovered),
        className: "custom-svg-marker",
        iconSize: [45, 45],
    });
};

type MapProps = {
    data: SearchItem[]
    onResultsUpdate: () => void
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    categoryColors: string[],
    setPage: () => void,
    hoveredItem: SearchItem,
    fly: boolean,
    setFly: React.Dispatch<React.SetStateAction<boolean>>,
    isAccomodationModal: boolean
}

const Map: React.FC<MapProps> = ({
    data,
    onResultsUpdate,
    setLoading,
    categoryColors,
    setPage,
    hoveredItem,
    fly,
    setFly,
    isAccomodationModal = false
}) => {
    const [selectedAddress, setSelectedAddress] = useState(null);
    const popupRef = useRef(null);
    const { t } = useTranslation(["common", "validation", "glossary"]);

    const [isRequesting, setIsRequesting] = useState(false);
    const debounceTimeout = useRef(null);

    const urlParams = new URLSearchParams(window.location.search);
    const boundsParam = urlParams.get("bounds");
    const centerParam = urlParams.get("center");
    const zoomParam = urlParams.get("zoom");
    const locale = usePage().props.locale;
    let initialPosition = [40.6736079234955, 24.635988993044382]; // Default position
    let initialZoom = 11; // Default zoom

    if (centerParam && zoomParam) {
        initialPosition = JSON.parse(centerParam);
        initialZoom = parseInt(zoomParam, 10);
    } else if (boundsParam) {
        const bounds = JSON.parse(boundsParam);
        const centerLat = (bounds._southWest.lat + bounds._northEast.lat) / 2;
        const centerLng = (bounds._southWest.lng + bounds._northEast.lng) / 2;
        initialPosition = [centerLat, centerLng];
        initialZoom = 11;
    }

    const createClusterCustomIcon = useCallback(
        (cluster) => {
            const markers = cluster.getAllChildMarkers();
            const count = cluster.getChildCount();
            let sizeClass = "w-12 h-12"; // default size
            if (count < 10) {
                sizeClass = "w-10 h-10";
            } else if (count < 100) {
                sizeClass = "w-12 h-12";
            } else {
                sizeClass = "w-14 h-14";
            }

            // Check if any marker's title and id matches the hovered item
            const isHovered =
                hoveredItem &&
                markers.some(
                    (marker) =>
                        marker.options.title === hoveredItem.name &&
                        marker.options.options.id === hoveredItem.id,
                );
            return L.divIcon({
                html: `<div class="flex items-center justify-center opacity-75 ${sizeClass} bg-primary text-white rounded-full ${isHovered ? "animate-bounce" : ""}">${count}</div>`,
                className: "custom-cluster",
                iconSize: L.point(45, 45, true),
            });
        },
        [hoveredItem],
    )

    const [mapState, setMapState] = useState({
        center: initialPosition,
        zoom: initialZoom,
    })

    const FlyToBounds = ({ markers }) => {
        const map = useMap();

        if (markers.length > 0 && !boundsParam && !centerParam && !zoomParam) {
            const bounds = L.latLngBounds(
                markers.map((marker) => [marker.latitude, marker.longitude]),
            );

            map.flyToBounds(bounds, { duration: 2 });
        }

        return null;
    }

    useEffect(() => {
        if (centerParam && zoomParam) {
            setMapState({
                center: JSON.parse(centerParam),
                zoom: parseInt(zoomParam, 10),
            });
        } else if (boundsParam) {
            const bounds = JSON.parse(boundsParam);
            const centerLat =
                (bounds._southWest.lat + bounds._northEast.lat) / 2;
            const centerLng =
                (bounds._southWest.lng + bounds._northEast.lng) / 2;
            setMapState({
                center: [centerLat, centerLng],
                zoom: 11,
            });
        }
    }, [boundsParam, centerParam, zoomParam]);

    const handleMapClick = (e) => {
        const { lat, lng } = e.latlng;
    };

    const handleMoveEnd = (e) => {

        if (isRequesting) return;
        if (isAccomodationModal) return

        const zoom = e.target.getZoom();
        const center = e.target.getCenter();
        const bounds = e.target.getBounds();

        setMapState({
            center: [center.lat, center.lng],
            zoom: zoom,
        });
        setFly(true);
        const existingParams = new URLSearchParams(window.location.search);
        existingParams.set(
            "bounds",
            JSON.stringify({
                _southWest: bounds.getSouthWest(),
                _northEast: bounds.getNorthEast(),
            }),
        );
        existingParams.set("center", JSON.stringify([center.lat, center.lng]));
        existingParams.set("zoom", zoom);
        existingParams.set("page", 1); // Set page to 1

        setIsRequesting(true);
        setLoading(true);
        setPage(1); // Update the current page to 1 in the parent component
        router.get(
            `/${locale}/search?${existingParams.toString()}`,
            {},
            {
                onSuccess: (page) => {
                    if (page.props.results && page.props.results.data) {
                        setIsRequesting(false);
                        onResultsUpdate(page.props.results.data);
                    }
                },
                onFinish: () => {
                    setIsRequesting(false);
                    setLoading(false);
                },
                preserveState: true,
            },
        );

        // Close the popup when the map changes bounds
        setSelectedAddress(null);
    }

    const debounceHandleMoveEnd = (e) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => handleMoveEnd(e), 100);
    };

    const isMobile = () => window.innerWidth <= 767;

    const { isOpen, onOpen, onOpenChange } = useDisclosure();

    const createMobilePopup = (address) => (
        // <div
        //     className="fixed bottom-0 left-0 right-0 bg-white p-4 shadow-lg z-50 rounded-t-lg"
        //     ref={popupRef}
        // >
        //     <div className="text-center">
        //         <div className="w-full flex justify-end">
        //             <Button
        //                 variant="light"
        //                 className="mb-4"
        //                 onClick={() => setSelectedAddress(null)}
        //                 isIconOnly
        //                 startContent={<img src={close} />}
        //             />
        //         </div>

        //         <div className="relative rounded-lg bg-blue-950">
        //             <img
        //                 src={address.image}
        //                 className="w-full h-auto opacity-80 object-cover mx-auto mb-2 rounded-lg"
        //             />
        //             <div className="absolute top-2 right-2">
        //                 <Star item={address} />
        //             </div>
        //         </div>
        //         <p className="text-lg">{address.name}</p>
        //         <TransLink
        //             url={`${address.slug.prefix}/${address.slug.key}`}
        //             className="border mx-auto  hover:bg-[#1D24CA] hover:!text-white rounded-full h-10 flex items-center bg-white !text-[#1D26CA] justify-center"
        //         >
        //             <div className="m-4 font-semibold">{t("visit_now", { ns: "glossary" })}</div>
        //             <span className="absolute right-4">
        //                 <ReactSVG
        //                     src={arrowsearch}
        //                     className="w-10 h-10"
        //                     beforeInjection={(svg) => {
        //                         svg.setAttribute(
        //                             "style",
        //                             "width: 100%; height: 100%;",
        //                         );
        //                         svg.querySelectorAll("path").forEach((path) => {
        //                             path.setAttribute("fill", "currentColor");
        //                         });
        //                     }}
        //                 />
        //             </span>
        //         </TransLink>
        //         <Directions

        //             iconColor='white'
        //             class="w-full !my-0! mt-2 !rounded-full h-10"
        //             destinationLat={address.latitude}
        //             destinationLng={address.longitude}
        //         />
        //     </div>
        // </div>
        (<Modal isOpen={isOpen} onOpenChange={onOpenChange}>
            <ModalContent>
                {(onClose) => (
                    <>
                        <ModalHeader className="flex flex-col gap-1">

                        </ModalHeader>
                        <ModalBody>
                            <div className="relative rounded-lg bg-blue-950">
                                <img
                                    src={address.image}
                                    className="w-full h-auto opacity-80 object-cover mx-auto mb-2 rounded-lg"
                                />
                                <div className="absolute top-2 right-2">
                                    <Star item={address} />
                                </div>
                            </div>
                            <p className="text-lg">{address.name}</p>
                            <TransLink
                                url={`${address.slug.prefix}/${address.slug.key}`}
                                className="border hover:bg-[#1D24CA] hover:!text-white rounded-full h-10 flex items-center bg-white !text-[#1D26CA] justify-center"
                            >
                                <div className="m-4 font-semibold">{t("visit_now", { ns: "glossary" })}</div>
                                <span className="absolute right-4">
                                    <ReactSVG
                                        src={arrowsearch}
                                        className="w-10 h-10"
                                        beforeInjection={(svg) => {
                                            svg.setAttribute(
                                                "style",
                                                "width: 100%; height: 100%;",
                                            );
                                            svg.querySelectorAll("path").forEach((path) => {
                                                path.setAttribute("fill", "currentColor");
                                            });
                                        }}
                                    />
                                </span>
                            </TransLink>
                            <Directions

                                iconColor='white'
                                class="w-full !my-0! mt-2 !rounded-full h-10"
                                destinationLat={address.latitude}
                                destinationLng={address.longitude}
                            />

                        </ModalBody>
                        <ModalFooter>
                            {/* <Button color="danger" variant="light" onPress={onClose}>
                                Close
                            </Button>
                            <Button color="primary" onPress={onClose}>
                                Action
                            </Button> */}
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal >)
    );

    return (
        <>
            <MapContainer
                center={mapState.center}
                zoom={mapState.zoom}
                style={{ height: "100%", zIndex: 1 }}
                maxZoom={18}
                attributionControl={false}
            >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {!isAccomodationModal &&
                    <MapEventsHandler
                        handleMapClick={handleMapClick}
                        handleMoveEnd={debounceHandleMoveEnd}
                    />}

                {!boundsParam &&
                    !centerParam &&
                    !zoomParam && !isAccomodationModal &&
                    fly === false && <FlyToBounds markers={data} />}

                {isAccomodationModal && <FlyToBounds markers={data} />}

                <MarkerClusterGroup
                    key={hoveredItem?.name}
                    chunkedLoading
                    iconCreateFunction={createClusterCustomIcon}
                >
                    {data &&
                        data.map((address, index) => {

                            const markerColor =
                                categoryColors[address.model] || "#1D26CA"; // Get color based on the modelType

                            const isHovered =
                                hoveredItem &&
                                hoveredItem.slug?.prefix ===
                                address.slug?.prefix &&
                                hoveredItem.slug?.key === address.slug?.key &&
                                hoveredItem?.model === address.model;

                            return (
                                <Marker
                                    key={address.name}
                                    position={[
                                        address.latitude,
                                        address.longitude,
                                    ]}
                                    title={address.name}
                                    icon={createCustomIcon(
                                        markerColor,
                                        isHovered,
                                    )}

                                    options={{ id: address.id }}
                                    eventHandlers={{
                                        click: () => {
                                            setSelectedAddress(address);
                                            onOpen()
                                        }
                                    }}
                                    
                                >
                                    {!isMobile() && <PopupMap address={address} />}
                                </Marker>
                            );
                        })}
                </MarkerClusterGroup>
            </MapContainer>

            {isMobile() && selectedAddress && createMobilePopup(selectedAddress)}
        </>
    );
};

const MapEventsHandler = ({ handleMapClick, handleMoveEnd }) => {
    const map = useMap();

    useMapEvents({
        click: handleMapClick,
        dragend: handleMoveEnd,
        zoomanim: handleMoveEnd,
        movestart: () => {
            // map.closePopup();
        },
        dragstart: () => {
            map.closePopup();
        }

    });

    return null;
};

export default Map;
